import { PacketAction, PacketFamily, EoWriter, Item, type EoReader } from 'eolib';
import type { Client } from '@/client';
import { GameState } from '@/game-state';
import { clearWorldEnvironment, receiveWorldEnvironment, environmentNeedsRefresh } from '@/world-environment';
import { receivePrivateRoom } from '@/private-rooms';
import {receiveWorldTicket} from '@/world-tickets';
import {receiveMarket} from '@/marketplace';
import {receiveFlair} from '@/flair';
import {clearMercenary,receiveMercenary} from '@/mercenaries';
import {clearSummoning,receiveSummoning} from '@/summoning';
import {receiveDungeonContract,clearDungeonContract} from '@/dungeon-contracts';
import {receiveDungeonRankings} from '@/dungeon-leaderboard';

export const FAMILY_LIFE_SKILLS = 58 as PacketFamily;

export type LifeSkill = {
  key: string;
  name: string;
  level: number;
  experience: number;
  level_start_experience: number;
  next_level_experience: number;
};

export type LifeNode = {id:string;name:string;x:number;y:number;skill:string;level:number;duration_ms:number;ready_at:number;expires_at?:number;graphic:string;depleted_graphic:string};
export type LifeActionState={skill:string;ready_at:number;expires_at:number;started_at?:number;origin_x?:number;origin_y?:number;direction?:number;node:string;x:number;y:number;phase:string;player_id?:number;remaining?:number};
export type Achievement={key:string;title:string;description:string;unlocked:boolean;progress:number;required:number};
export type LifeSkillsSnapshot = { skills: LifeSkill[]; max_level: number; map_id?:number; profession?:string; error?: string;message?:string;nodes?:LifeNode[];actions?:LifeActionState[];action_ready_at?:number;action?:LifeActionState|null;server_now?:number;achievements?:Achievement[];selected_title?:string|null;tutorial?:{stage:string;instructions:string}|null;inventory?:{id:number;amount:number}[] };
export type LifeSkillsViewState = { snapshot?: LifeSkillsSnapshot; pending: boolean; message: string; campfire?:{map:number;id:string} };
type Store = { state: LifeSkillsViewState; listeners: Set<() => void>; timeout: number; registered: boolean };
const stores = new WeakMap<Client, Store>();
const professions = new WeakMap<Client, Map<number, {name:string;profession:string}>>();
type WorldQuery={map:number;at:number;received:boolean};
const worldQueries=new WeakMap<Client,WorldQuery>();
const worldNodes=new WeakMap<Client,Map<number,LifeNode[]>>();

export function getLifeWorld(client:Client):LifeNode[]{
  const received=worldNodes.get(client)?.get(client.mapId);
  if(received)return received;
  const snapshot=store(client).state.snapshot;
  return snapshot?.map_id===client.mapId?snapshot.nodes??[]:[];
}

export function openLifeCampfire(client:Client,id:string|null):void{
  const value=store(client);
  update(value,{...value.state,campfire:id?{map:client.mapId,id}:undefined});
}

export function syncLifeWorld(client:Client):void{
  if(client.state!==GameState.InGame||client.worldLoading)return;
  const now=Date.now(),last=worldQueries.get(client);
  // A map transition always gets its own request. Retry until the server
  // acknowledges this map; never let a dropped request leave the map empty.
  if(last?.map===client.mapId&&last.received&&now-last.at<60_000&&!environmentNeedsRefresh(client))return;
  if(last?.map===client.mapId&&now-last.at<1_500)return;
  worldQueries.set(client,{map:client.mapId,at:now,received:false});
  const writer=new EoWriter();writer.addString(JSON.stringify({action:'world_resources'}));
  try{client.bus.sendBuf(FAMILY_LIFE_SKILLS,PacketAction.Request,writer.toByteArray());}catch{/* Reconnect owns recovery. */}
}

export function getProfession(client:Client,playerId:number,name:string):string {
  if(playerId===client.playerId&&store(client).state.snapshot?.profession)return store(client).state.snapshot!.profession!;
  const entry=professions.get(client)?.get(playerId);
  if(entry?.name===name)return entry.profession;
  return playerId===client.playerId ? store(client).state.snapshot?.profession ?? '—' : '—';
}

function store(client: Client): Store {
  let value = stores.get(client);
  if (!value) {
    value = { state: { pending: false, message: '' }, listeners: new Set(), timeout: 0, registered: false };
    stores.set(client, value);
  }
  return value;
}

function update(value: Store, state: LifeSkillsViewState): void {
  value.state = state;
  for (const notify of value.listeners) notify();
}

export function getLifeSkills(client: Client): LifeSkillsViewState {
  return store(client).state;
}

export function getLifeCharacterAction(client:Client,playerId:number,now=Date.now()):LifeActionState|undefined {
  const s=store(client).state.snapshot;
  if(s?.map_id!==client.mapId)return;
  const action=playerId===client.playerId?s.action:s.actions?.find(a=>a.player_id===playerId);
  if(!action||now>=action.expires_at)return;
  const c=client.getCharacterById(playerId);
  if(!c||(action.origin_x!==undefined&&(c.coords.x!==action.origin_x||c.coords.y!==action.origin_y)))return;
  return action;
}

function refreshLifeAppearance(client:Client,before:LifeSkillsSnapshot|undefined,after:LifeSkillsSnapshot|undefined):void {
  const key=(s:LifeSkillsSnapshot|undefined)=>JSON.stringify((s?.actions??[]).filter(a=>a.skill!=='woodcutting').map(a=>a.player_id).sort());
  if(key(before)!==key(after))client.atlas?.refresh();
}

export function subscribeLifeSkills(client: Client, notify: () => void): () => void {
  const value = store(client);
  value.listeners.add(notify);
  return () => value.listeners.delete(notify);
}

export function requestLifeSkills(client: Client): boolean {
  const value = store(client);
  if (client.state !== GameState.InGame || value.state.pending) return false;
  update(value, { ...value.state, pending: true, message: '' });
  clearTimeout(value.timeout);
  value.timeout = window.setTimeout(() => {
    update(value, { ...value.state, pending: false, message: 'No response yet. Please refresh and try again.' });
  }, 6000);
  try {
    client.bus.sendBuf(FAMILY_LIFE_SKILLS, PacketAction.Request, new Uint8Array());
    return true;
  } catch {
    clearTimeout(value.timeout);
    update(value, { ...value.state, pending: false, message: 'Connection unavailable. Please reconnect.' });
    return false;
  }
}

export function sendLifeAction(client:Client,action:'start'|'finish'|'reel'|'cancel'|'title'|'make_fire_logs'|'make_fire_paper'|'cook_fish'|'bake_berries'|'cook_fish_all'|'bake_berries_all',node=''):boolean{
  const value=store(client);if(client.state!==GameState.InGame||(value.state.pending&&action!=='cancel'))return false;
  const writer=new EoWriter();writer.addString(JSON.stringify({action,node}));
  update(value,{...value.state,pending:true,message:''});clearTimeout(value.timeout);
  value.timeout=window.setTimeout(()=>update(value,{...value.state,pending:false,message:'No response. Refresh before trying again.'}),6000);
  try{client.bus.sendBuf(FAMILY_LIFE_SKILLS,PacketAction.Request,writer.toByteArray());return true;}
  catch{clearTimeout(value.timeout);update(value,{...value.state,pending:false,message:'Connection unavailable.'});return false;}
}

export function registerLifeSkills(client: Client): void {
  const value = store(client);
  if (!value.registered) {
    value.registered = true;
    client.subscribeStateChanged(state => {
    if (state !== GameState.InGame) {
      clearSummoning(client);
      clearMercenary(client);
      clearDungeonContract(client);
      clearWorldEnvironment(client);
      professions.delete(client);
      worldQueries.delete(client);
      worldNodes.delete(client);
        clearTimeout(value.timeout);
        update(value, { pending: false, message: '' });
      }
    });
  }

  client.bus.registerPacketHandler(FAMILY_LIFE_SKILLS, PacketAction.Reply, (reader: EoReader) => {
    let data: LifeSkillsSnapshot;
    try {
      data = JSON.parse(reader.getString()) as LifeSkillsSnapshot;
    } catch {
      return;
    }
    const extension=data as unknown as {kind?:string;player_id?:number;name?:string;profession?:string;reason?:string};
    if(extension.kind==='quest_retired'){window.dispatchEvent(new CustomEvent('lulseo-quest-retired',{detail:data}));return;}
    if(extension.kind==='world_environment'){receiveWorldEnvironment(client,data);return;}
    if(extension.kind==='private_room'){receivePrivateRoom(client,data);return;}
    if(extension.kind==='world_ticket'){receiveWorldTicket(client,data);return;}
    if(extension.kind==='marketplace'){receiveMarket(client,data);return;}
    if(extension.kind==='flair'){receiveFlair(client,data);return;}
    if(extension.kind==='mercenary'){receiveMercenary(client,data);return;}
    if(extension.kind?.startsWith('summon')){receiveSummoning(client,data);return;}
    if(extension.kind==='dungeon_contract'){receiveDungeonContract(client,data);return;}
    if(extension.kind==='dungeon_leaderboard'){receiveDungeonRankings(client,data);return;}
    if(extension.kind==='profession'){
      if(!Number.isInteger(extension.player_id)||typeof extension.name!=='string'||typeof extension.profession!=='string')return;
      let values=professions.get(client);if(!values){values=new Map();professions.set(client,values);}
      values.set(extension.player_id!,{name:extension.name,profession:extension.profession});
      if(extension.player_id===client.playerId&&value.state.snapshot){
        value.state={...value.state,snapshot:{...value.state.snapshot,profession:extension.profession}};
      }
      for(const notify of value.listeners)notify();
      return;
    }
    if(data.map_id!==undefined&&data.map_id!==client.mapId)return;
    const world=(extension.kind==='life_world');
    const cancelled=extension.kind==='life_action_cancelled';
    if(world&&(!Number.isInteger(data.map_id)||!Array.isArray(data.nodes)||!data.nodes.every(node=>
      typeof node.id==='string'&&typeof node.name==='string'&&Number.isInteger(node.x)&&Number.isInteger(node.y)
      &&typeof node.skill==='string'&&Number.isFinite(node.ready_at)
      &&['oak','stump','berry_bush','berry_bush_empty','fishing_spot','fishing_spot_empty','campfire'].includes(node.graphic)
      &&['oak','stump','berry_bush','berry_bush_empty','fishing_spot','fishing_spot_empty','campfire'].includes(node.depleted_graphic))))return;
    if (!world && !cancelled && !data.error && (!Array.isArray(data.skills) || data.max_level !== 99)) return;
    // Display countdowns against the local clock; reward timing stays server-owned.
    if(Number.isFinite(data.server_now)){
      const offset=Date.now()-data.server_now!;
      if(data.action_ready_at)data.action_ready_at+=offset;
      if(data.action){data.action.ready_at+=offset;data.action.expires_at+=offset;if(data.action.started_at!==undefined)data.action.started_at+=offset;}
      for(const action of data.actions??[]){action.ready_at+=offset;action.expires_at+=offset;if(action.started_at!==undefined)action.started_at+=offset;}
      for(const node of data.nodes??[]){if(node.ready_at)node.ready_at+=offset;if(node.expires_at)node.expires_at+=offset;}
    }
    if(cancelled){
      if(!Number.isInteger(extension.player_id)||!Array.isArray(data.actions))return;
      const own=extension.player_id===client.playerId,before=value.state.snapshot;
      if(own)clearTimeout(value.timeout);
      const snapshot={skills:[],max_level:99,...before,map_id:data.map_id,actions:data.actions,
        ...(own?{action:null,action_ready_at:0}:{})} as LifeSkillsSnapshot;
      update(value,{...value.state,snapshot,...(own?{pending:false,message:'',campfire:undefined}:{})});
      refreshLifeAppearance(client,before,snapshot);
      return;
    }
    if(Array.isArray(data.nodes)){
      let maps=worldNodes.get(client);if(!maps){maps=new Map();worldNodes.set(client,maps);}
      maps.set(data.map_id!,data.nodes);
      worldQueries.set(client,{map:data.map_id!,at:Date.now(),received:true});
    }
    if(world){
      if(!Array.isArray(data.nodes))return;
      const before=value.state.snapshot;
      update(value,{...value.state,snapshot:{skills:[],max_level:99,...value.state.snapshot,
        map_id:data.map_id,nodes:data.nodes,actions:data.actions,action:data.action,action_ready_at:data.action_ready_at}});
      refreshLifeAppearance(client,before,value.state.snapshot);
      return;
    }
    clearTimeout(value.timeout);
    if(data.inventory && Array.isArray(data.inventory) && data.inventory.every(x=>Number.isInteger(x.id)&&x.id>0&&Number.isSafeInteger(x.amount)&&x.amount>0)){
      client.inventoryController.items=data.inventory.map(x=>{const i=new Item();i.id=x.id;i.amount=x.amount;return i;});
      client.inventoryController.refreshEnhancementDisplay();
    }
    // An early finish is not a cancellation; retain its completion button.
    if(data.action_ready_at===undefined && data.error==='Still working' && value.state.snapshot?.action_ready_at){data.action_ready_at=value.state.snapshot.action_ready_at;}
    const before=value.state.snapshot;
    const snapshot=Array.isArray(data.skills)?data:before?{...before,...data}:undefined;
    update(value, { ...value.state, snapshot, pending: false, message: data.error ?? data.message ?? '' });
    refreshLifeAppearance(client,before,snapshot);
  });
}
